import CircularProgress from "@mui/material/CircularProgress";

import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { Box, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useState, useContext, useCallback } from "react";
import {
  useDownloadAction,
  useAction,
  useActionAttachments,
} from "./apiAction";
import { UserContext } from "components/context/UserProvider";
import Button from "components/ui/atoms/Button";
import Highlight from "components/ui/atoms/Highlight";
import SectionGrid from "components/ui/atoms/SectionGrid";
import { PersonTag } from "components/ui/atoms/Tag";
import TypographyWithMiss from "components/ui/atoms/TypographyWithMiss";
import { LayoutContext } from "components/ui/molecules/Layout";
import AttachmentDownloadButton from "components/ui/atoms/AttachmentDownloadButton";

const ActionLinks = ({ actionLinkType, actionLinks }) => {
  return (
    actionLinks.length > 0 && (
      <Grid size={{ mb: 2 }}>
        <Typography variant="body1">{actionLinkType}</Typography>
        <Stack
          direction="row"
          justifyContent="flex-start"
          flexWrap="wrap"
          my={1}
          sx={{ gap: 1 }}
        >
          {actionLinks.map((action_link, index) => {
            return (
              <Link
                to={`/people/${action_link.people_id}/`}
                key={`people_link_${index}`}
                target="_blank"
              >
                <PersonTag
                  person={{ best_email_address: action_link.email_address }}
                  style={{ cursor: "pointer" }}
                />
              </Link>
            );
          })}
        </Stack>
      </Grid>
    )
  );
};

const ActionView = ({ onCancel, actionId }) => {
  const { hasPermission } = useContext(UserContext);
  const theme = useTheme();
  const { data: action, status } = useAction(actionId);
  const [downloadId, setDownloadId] = useState(null);
  const { data: downloadData, status: downloadStatus } =
    useDownloadAction(downloadId);

  const { data: attachments, status: attachmentStatus } = useActionAttachments(
    actionId,
    { enabled: hasPermission("actions.view_attachments") },
  );
  const startDownload = () => {
    setDownloadId(actionId);
  };

  const readable = useCallback((fileSizeInBytes) => {
    var i = -1;
    var byteUnits = [" kB", " MB", " GB", " TB", "PB", "EB", "ZB", "YB"];
    do {
      fileSizeInBytes /= 1024;
      i++;
    } while (fileSizeInBytes > 1024);

    return Math.max(fileSizeInBytes, 0.1).toFixed(1) + byteUnits[i];
  }, []);

  if (status === "error") return <>Error</>;
  if (status !== "success") return <></>;

  return (
    <>
      <Box
        p={2}
        sx={{
          height: 64,
          display: "flex",
          alignItems: "center",
          flexWrap: "nowrap",
          borderBottom: 1,
          borderColor: "black",
          boxSizing: "border-box",
        }}
      >
        <Tooltip title="esc">
          <IconButton onClick={onCancel}>
            <KeyboardDoubleArrowRightIcon />
          </IconButton>
        </Tooltip>
        <Box sx={{ ml: 1 }}>
          <Highlight color={theme.palette_custom.yellow}>
            {action?.title}
          </Highlight>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            ml: "auto",
          }}
        >
          <Typography variant="caption">
            {dayjs(action?.date).format("DD/MM/YYYY HH:MM")}
          </Typography>
        </Box>
      </Box>
      <Stack spacing={2} p={2}>
        <ActionLinks
          actionLinkType="From"
          actionLinks={action?.action_links?.filter(
            (action_link) => action_link?.link_type === "from",
          )}
        />
        <ActionLinks
          actionLinkType="To"
          actionLinks={action?.action_links?.filter(
            (action_link) => action_link?.link_type === "to",
          )}
        />
        <ActionLinks
          actionLinkType="Cc"
          actionLinks={action?.action_links?.filter(
            (action_link) => action_link?.link_type === "cc",
          )}
        />
        <ActionLinks
          actionLinkType="Cci"
          actionLinks={action?.action_links?.filter(
            (action_link) => action_link?.link_type === "cci",
          )}
        />
      </Stack>
      <Grid container spacing={1} p={2}>
        {hasPermission("actions.view_snipet") && (
          <SectionGrid title="Snippet" size={12}>
            <TypographyWithMiss
              variant="body1"
              whiteSpace="pre-line"
              isMissing={action.snippet == ""}
            >
              {action.snippet}
            </TypographyWithMiss>
          </SectionGrid>
        )}
        {hasPermission("actions.view_attachments") && (
          <SectionGrid title="Attachments" size={12}>
            {attachmentStatus === "success" &&
              attachments.map((attachment, index) => {
                return (
                  <Stack
                    key={`attachment_${index}`}
                    direction={"row"}
                    justifyContent={"space-between"}
                    sx={{ alignItems: "center" }}
                  >
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      {attachment.attachment_name}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        flexGrow: 0,
                        flexShrink: 0,
                        width: "60px",
                        textAlign: "center",
                      }}
                    >
                      {readable(attachment.attachment_size)}
                    </Typography>
                    <Box
                      sx={{
                        flexGrow: 0,
                        flexShrink: 0,
                        textAlign: "right",
                      }}
                    >
                      <AttachmentDownloadButton attachment={attachment} />
                    </Box>
                  </Stack>
                );
              })}
            {attachmentStatus === "success" && attachments.length === 0 && (
              <Typography variant="body1">No attachments</Typography>
            )}
            {attachmentStatus === "loading" ||
              (attachmentStatus === "pending" && (
                <CircularProgress size="0.9em" style={{ padding: "5px" }} />
              ))}
            {attachmentStatus !== "loading" &&
              attachmentStatus !== "success" &&
              attachmentStatus !== "pending" && (
                <Typography variant="body1">
                  Status = {attachmentStatus}
                </Typography>
              )}
          </SectionGrid>
        )}

        {hasPermission("app.download_action") && (
          <>
            <Button onClick={startDownload} sx={{ ml: "auto", mt: 4 }}>
              Download
            </Button>
            {downloadStatus === "success" && (
              <a
                href={URL.createObjectURL(
                  new Blob([downloadData], {
                    type: "application/octet-stream",
                  }),
                )}
                download={`${dayjs().format("YYYY-MM-DD")} - ${
                  action?.title
                }.eml`}
                style={{ display: "none" }}
                ref={(link) =>
                  link && link.click() && link.remove() && setDownloadId(null)
                }
              />
            )}
          </>
        )}
      </Grid>
    </>
  );
};

export default ActionView;
